<template>
  <div class="rmaForm">
    <div v-if="loading" class="loader loader-positioning" />
    <div v-if="$store.state.rma.model.make.usesCoupons" id="coupon-content">
      <p><span v-html="$mlGet('warrentyInformation-coupon-header')"></span></p>
      <div id="coupon-form">
        <input
          type="text"
          :placeholder="$mlGet('warrentyInformation-coupon-placeholder')"
          v-model="$store.state.rma.transportCouponCode"
          class="inputField"
        />
        <button class="button" @click="checkCouponCode()">
          <i class="fa fa-check" />
        </button>
      </div>
    </div>
    <form v-on:submit.prevent style="text-align: left">
      <label for="rmaReason">{{ $mlGet("rmaReason-label") }}</label>
      <select name="rmaReason" v-model="$store.state.rma.reasonForReturn">
        <option
          v-for="reason in rmaReasonOptions"
          :key="reason.id"
          :value="reason.id"
        >
          {{ $mlGet(reason.languageItemKey) }}
        </option>
      </select>
      <br />

      <div v-if="fieldCheckPriceQuota()">
        <div>
          <label for="priceSelect">{{
            $mlGet("warrentyInformation-price-label")
          }}</label>
          <select id="priceSelect" v-model="$store.state.rma.priceQuotation">
            <option
              v-for="price in priceResults"
              :key="price.id"
              :value="price.priceEuro"
            >
              € {{ price.priceEuro }}
            </option>
          </select>
        </div>
      </div>

      <div
        v-if="
          $store.state.rma.dealerOptionsPickupDirectly === true ||
            $store.state.rma.dealerOptionsRetourDirectly === true
        "
        style="margin: 10px 0;"
      >
        <NullableRadio
          id="contact-directly-input"
          style="grid-column: 1;"
          :questionString="$mlGet('input-dealerOptionsContactDirectly')"
          v-model="$store.state.rma.dealerOptionsContactDirectly"
        />
      </div>

      <!-- If warrenty checkbox is clicked: -->
      <div v-if="fieldCheckFormInfo()">
        <p v-if="$store.state.rma.reasonForReturn == 1">
          <span
            v-html="$mlGet('warrentyInformation-informationBox-non-Warrenty')"
          ></span>
        </p>
        <br />
        <b>{{ $mlGet("warrentyInformation-header") }}</b>
        <br />
        <label for="dateOfPurchase">{{
          $mlGet("warrentyInformation-dateOfPurchase-label")
        }}</label>
        <input
          v-model="$store.state.rma.dateOfPurchase"
          type="date"
          name="dateOfPurchase"
          id="input-dateofpurchase"
        />
        <br />
        <div class="uploadWrapper">
          <label for="purchaseReceipt">{{
            $mlGet("warrentyInformation-upload-label")
          }}</label>
          <label class="uploadLabel" for="purchaseReceipt">{{
            $mlGet("warrentyInformation-upload-button")
          }}</label>
          <input
            type="file"
            ref="purchaseReceipt"
            id="purchaseReceipt"
            @change="uploadHandler()"
            class="uploadButton"
          />
          <div id="uploadedBox" v-if="$store.state.rma.receipt.originalname">
            {{ $mlGet("warrentyInformation-upload-files-label") }}
            <i class="fa fa-file" aria-hidden="true" />
            {{ $store.state.rma.receipt.originalname }}
          </div>
        </div>
      </div>
    </form>
    <div class="information-box">
      <span v-html="$mlGet('warrentyInformation-informationBox')"></span>
    </div>
  </div>
</template>

<script lang="ts">
import { FormFile } from "@/types";
import { AxiosError } from "axios";
import { Component, Vue } from "vue-property-decorator";

import { highlightElementQuerySelector } from "@/helpers/alerts";
import { PageChangeContext, PageName } from "@/views/CreateRma.vue";
import NullableRadio from "../Input/NullableRadio.vue";
import * as Sentry from "@sentry/vue";

@Component({
  components: {
    NullableRadio
  }
})
export default class ModelInformationSide extends Vue {
  public loading = false;
  public priceResults: Array<{ id: number; priceEuro: number }> = [];
  public priceQuotation = "";
  public rmaReasonOptions: Array<any> = [];

  public testValue = -1;

  $refs!: {
    purchaseReceipt: HTMLFormElement;
  };

  async mounted(): Promise<void> {
    await this.getPrices();
    await this.getRmaReasons();

    if (this.$store.state.rma.priceQuotation == 0) {
      this.$store.state.rma.priceQuotation = this.priceResults[0].priceEuro;
    }
  }

  async checkCouponCode(): Promise<void> {
    const coupon = this.$store.state.rma.transportCouponCode;
    const response = await this.$axios
      .put("/transport/coupon", {
        couponCode: coupon,
        makeId: this.$store.state.rma.model.make.fmId
      })
      .catch(() => {
        this.$swal(
          this.$mlGet("coupon-check-deny-title"),
          this.$mlGet("coupon-check-deny-body"),
          "error"
        );
      });
    if (response && response.data) {
      Sentry.metrics.count("used_valid_coupon", 1);
      this.$swal(
        this.$mlGet("coupon-check-success-title"),
        this.$mlGet("coupon-check-success-body"),
        "success"
      );
      this.getRmaReasonGroup();
    }
  }

  fieldCheckFormInfo(): boolean | void {
    let answer = false;
    const reason = this.$store.state.rmaReasons.find(
      (reason: { id: number }) => {
        return reason.id == this.$store.state.rma.reasonForReturn;
      }
    );

    if (reason && reason.showFields) answer = true;
    return answer;
  }

  fieldCheckPriceQuota(): boolean | void {
    let answer = false;
    const reason = this.$store.state.rmaReasons.find(
      (reason: { id: number }) => {
        return reason.id == this.$store.state.rma.reasonForReturn;
      }
    );

    if (reason && reason.needPriceQuotation) answer = true;
    return answer;
  }

  async getPrices(): Promise<void> {
    const response = await this.$axios.get(
      `/rma/priceQuotations/${this.$store.state.rma.model.fmId}`
    );
    this.priceResults = response ? response.data : [];
  }

  async getRmaReasons(): Promise<void> {
    this.loading = true;

    try {
      const reasons = await this.$axios.get(
        "/rma/reasons/" + this.$store.state.rma.model.fmId
      );
      this.rmaReasonOptions = reasons.data ? reasons.data : [];
      this.loading = false;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status == 404) {
        this.$swal({
          title: this.$mlGet("WarrentyInfo-error-noReasons-title"),
          icon: "error",
          html: this.$mlGet("WarrentyInfo-error-noReasons-text")
        });
      }

      this.loading = false;
    }
  }

  async getRmaReasonGroup(): Promise<void> {
    this.loading = true;

    try {
      const reasons = await this.$axios.get("/rma/reasons/");
      this.rmaReasonOptions = reasons.data ? reasons.data : [];
      this.loading = false;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status == 404) {
        this.$swal({
          title: this.$mlGet("WarrentyInfo-error-noReasons-title"),
          icon: "error",
          html: this.$mlGet("WarrentyInfo-error-noReasons-text")
        });
      }

      this.loading = false;
    }
  }

  uploadHandler(): void {
    const file = this.$refs.purchaseReceipt.files[0];
    const formData = new FormData();
    formData.append("photo", file);

    this.$axios
      .post("/rma/file", formData, {
        headers: {
          "Content-Type": "multipart-formdata"
        }
      })
      .then(response => {
        const file: FormFile = (({
          originalname,
          filename,
          mimetype,
          size
        }) => ({ originalname, filename, mimetype, size }))(response.data.file);

        // TODO: Changed this to a store commit. This could be a lot of work.
        this.$store.state.rma.receipt = file;
        this.$forceUpdate();

        Sentry.metrics.count("warranty_file_upload", 1);

        this.$swal({
          icon: "success",
          title: this.$mlGet("fileUpload-success-head"),
          showConfirmButton: false,
          timer: 1500
        });
      })
      .catch(error => {
        this.$swal(
          this.$mlGet("fileUpload-error-head"),
          error?.response?.data?.error ?? "Upload Error",
          "error"
        );
      });
  }

  public async beforePageLeave(context: PageChangeContext): Promise<boolean> {
    if (context.isReturnOperation) return true;

    const rmaReasonId = this.$store.state.rma.reasonForReturn;
    if (this.$store.state.rma.reasonForReturn == "") {
      highlightElementQuerySelector(
        "#rmaFormViewWrapper > div > form > select"
      );
      return false;
    }

    if (this.$store.getters.usesDealerCustomOptions) {
      if (this.$store.state.rma.dealerOptionsContactDirectly == -1) {
        highlightElementQuerySelector("#contact-directly-input");
        return false;
      }
    }

    const reason = this.$store.state.rmaReasons.find(
      (reason: any) => reason.id == rmaReasonId
    );
    if (
      reason.needFields === true &&
      this.$store.state.rma.dateOfPurchase === ""
    ) {
      highlightElementQuerySelector("#input-dateofpurchase");
      return false;
    }

    return true;
  }

  public async afterPageEnter(context: PageChangeContext): Promise<void> {
    return;
  }

  public async onPageLeave(context: PageChangeContext): Promise<PageName> {
    return "RmaReason";
  }
}
</script>

<style scoped>
.ModelInformationSide {
  margin-top: 2ex;
  width: 100%;
}

input {
  border: 1px solid lightgray;
  box-sizing: border-box;
  padding: 5px;
  font-style: italic;
  margin: 5px 0;
  width: 100%;
}

select {
  border: 1px solid lightgray;
  background-color: white;
  box-sizing: border-box;
  padding: 5px;
  font-style: italic;
  margin: 5px 0;
  width: 100%;
}

label {
  margin: 0 20px 0 0;
}

.uploadWrapper {
  margin: 10px 0;
}

.uploadButton {
  visibility: hidden;
}

.uploadLabel {
  background-color: #da6f38 !important;
  padding: 4px 8px;
  color: white;
  border-radius: 5px;
  font-weight: bold;
  margin: 10px;
  cursor: pointer;
}

#uploadedBox {
  margin: 7px 0;
}

#priceSelect {
  width: 10ex;
}

.button {
  border: none;
  background-color: #da6f38;
  color: white;
  font-size: 110%;
  padding: 4px;
  height: 3ex;
  margin: auto 5px;
  cursor: pointer;
}

#coupon-content {
  padding-bottom: 2ex;
}

#coupon-content p {
  padding: 0;
  margin: 0;
}

#coupon-form {
  display: flex;
}
</style>
